import { Copy, MessageCircle, Pencil } from 'lucide-react-native';
import { StyleSheet } from 'react-native';
import { Badge, Box, HStack, Text, VStack } from '../../../components/primitives';
import { Button } from '../../../components/primitives/Button';
import { colors } from '../../../theme/colors';
import { tokens } from '../../../theme/tokens';

interface WhatsAppDraftCardProps {
  draft: string;
  language: string;
  charCount: number;
  onCopy?: (draft: string) => void;
  onEdit?: (draft: string) => void;
}

const WHATSAPP_CHAR_LIMIT = 1024;

export function WhatsAppDraftCard({
  draft,
  language,
  charCount,
  onCopy,
  onEdit,
}: Readonly<WhatsAppDraftCardProps>) {
  const isNearLimit = charCount > WHATSAPP_CHAR_LIMIT * 0.9;

  return (
    <Box
      borderRadius="md"
      padding="sm"
      backgroundColor={colors.surface.row}
      style={styles.card}
      accessibilityRole="none"
    >
      <VStack spacing="xs">
        <HStack spacing="sm" align="center" justify="between">
          <HStack spacing="xs" align="center">
            <MessageCircle size={14} color={colors.status.success.main} strokeWidth={2} />
            <Text variant="caption" color={colors.text.primary} style={styles.title}>
              WhatsApp draft
            </Text>
          </HStack>
          <Badge color="cyan" variant="subtle" size="sm">
            {language}
          </Badge>
        </HStack>

        <Text variant="body" color={colors.text.primary} style={styles.draftText}>
          {draft}
        </Text>

        <Text
          variant="caption"
          color={isNearLimit ? colors.status.warning.main : colors.text.tertiary}
        >
          {charCount}/{WHATSAPP_CHAR_LIMIT} characters
        </Text>

        <HStack spacing="xs" justify="end" style={styles.actions}>
          <Button
            variant="ghost"
            size="sm"
            onPress={() => onEdit?.(draft)}
            accessibilityLabel="Edit draft"
          >
            <HStack spacing="xxs" align="center">
              <Pencil size={12} color={colors.text.secondary} strokeWidth={2} />
              <Text variant="caption" color={colors.text.secondary}>
                Edit
              </Text>
            </HStack>
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onPress={() => onCopy?.(draft)}
            accessibilityLabel="Copy draft"
          >
            <HStack spacing="xxs" align="center">
              <Copy size={12} color={colors.text.primary} strokeWidth={2} />
              <Text variant="caption" color={colors.text.primary}>
                Copy
              </Text>
            </HStack>
          </Button>
        </HStack>
      </VStack>
    </Box>
  );
}

const styles = StyleSheet.create({
  card: {
    borderWidth: 1,
    borderColor: colors.border.subtle,
    marginTop: tokens.spacing.xxs,
  },
  title: {
    fontWeight: '600',
  },
  draftText: {
    lineHeight: 20,
  },
  actions: {
    marginTop: tokens.spacing.xxs,
  },
});
